import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { reportsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { ArrowLeft, Receipt, Wallet, TrendingUp, FileText } from 'lucide-react';
import { DateRangeBar, ExportBar, KPICard, ReportLoader, ReportEmpty, ReportError, useCurrency, downloadCsv } from '../../components/reports/ReportKit';
import { printReportDocument } from '../../lib/printReport';

const VatReport = () => {
    const { t, i18n } = useTranslation(['entities', 'common']);
    const fmtMoney = useCurrency();
    const [filters, setFilters] = useState({ start: null, end: null, rate: 'all', sale_type: 'all' });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // default: this quarter
        const now = new Date();
        const q = Math.floor(now.getMonth() / 3);
        setFilters((f) => ({
            ...f,
            start: new Date(now.getFullYear(), q * 3, 1),
            end: new Date(now.getFullYear(), q * 3 + 3, 0),
        }));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (filters.start) params.start_date = format(filters.start, 'yyyy-MM-dd');
            if (filters.end) params.end_date = format(filters.end, 'yyyy-MM-dd');
            if (filters.rate !== 'all') params.rate = filters.rate;
            if (filters.sale_type !== 'all') params.sale_type = filters.sale_type;
            const { data: res } = await reportsAPI.vatAnalytics(params);
            setData(res);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
            toast.error(t('common:toasts.load_failed'));
        } finally {
            setLoading(false);
        }
    }, [filters, t]);

    useEffect(() => { if (filters.start && filters.end) load(); }, [load, filters.start, filters.end]);

    const setPreset = (days) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) { start.setHours(0, 0, 0, 0); } else start.setDate(start.getDate() - days);
        setFilters((f) => ({ ...f, start, end }));
    };

    const exportCsv = () => {
        if (!data?.by_rate?.length) return toast.error(t('entities:reports.toasts.nothing_to_export', { defaultValue: 'Nothing to export' }));
        const summary = data.by_rate.map((b) => ({
            rate: `${b.rate}%`,
            ht: b.ht,
            tva: b.tva,
            ttc: b.ttc,
            invoice_count: b.invoice_count,
        }));
        const columns = [
            { key: 'rate', label: t('entities:reports.vat.rate', { defaultValue: 'Rate' }) },
            { key: 'ht', label: t('entities:reports.vat.ht', { defaultValue: 'Net HT' }) },
            { key: 'tva', label: t('entities:reports.vat.tva', { defaultValue: 'VAT' }) },
            { key: 'ttc', label: t('entities:reports.vat.ttc', { defaultValue: 'Gross TTC' }) },
            { key: 'invoice_count', label: t('entities:reports.columns.invoice') },
        ];
        downloadCsv(`vat-report-${format(filters.start, 'yyyy-MM-dd')}_${format(filters.end, 'yyyy-MM-dd')}.csv`, columns, summary);
        toast.success(t('entities:reports.toasts.exported'));
    };

    const handlePrint = () => {
        if (!data?.by_rate?.length) {
            toast.error(t('entities:reports.toasts.nothing_to_export', { defaultValue: 'Nothing to export' }));
            return;
        }
        const lang = i18n.language || 'fr';
        const moneyTxt = (v) => fmtMoney(v).replace(/\u00a0/g, ' ');
        const period = (filters.start && filters.end)
            ? `${format(filters.start, 'dd MMM yyyy')} — ${format(filters.end, 'dd MMM yyyy')}`
            : '';
        const kp = data?.kpis || {};
        const rateRows = data.by_rate.map((b) => `
            <tr>
                <td style="font-weight:600;">${b.rate}%</td>
                <td class="num">${moneyTxt(b.ht || 0)}</td>
                <td class="num">${moneyTxt(b.tva || 0)}</td>
                <td class="num" style="font-weight:600;">${moneyTxt(b.ttc || 0)}</td>
                <td class="num">${b.invoice_count ?? 0}</td>
            </tr>
        `).join('');
        const metaHtml = `
            <div class="kpi-strip">
                <div class="kpi"><span class="k">HT</span><span class="v">${moneyTxt(kp.total_ht)}</span></div>
                <div class="kpi accent"><span class="k">TVA</span><span class="v">${moneyTxt(kp.total_tva)}</span></div>
                <div class="kpi success"><span class="k">TTC</span><span class="v">${moneyTxt(kp.total_ttc)}</span></div>
                <div class="kpi"><span class="k">${lang.startsWith('fr') ? 'Factures' : 'Invoices'}</span><span class="v">${kp.invoice_count ?? 0}</span></div>
            </div>
        `;
        const contentHtml = `
            <table class="report-table">
                <thead><tr>
                    <th>${lang.startsWith('fr') ? 'Taux' : 'Rate'}</th>
                    <th class="num">HT</th>
                    <th class="num">TVA</th>
                    <th class="num">TTC</th>
                    <th class="num">${lang.startsWith('fr') ? 'Nb. Factures' : 'Invoice Count'}</th>
                </tr></thead>
                <tbody>${rateRows}</tbody>
                <tfoot>
                    <tr>
                        <td style="text-transform:uppercase; letter-spacing:1px;">Total</td>
                        <td class="num">${moneyTxt(kp.total_ht)}</td>
                        <td class="num">${moneyTxt(kp.total_tva)}</td>
                        <td class="num">${moneyTxt(kp.total_ttc)}</td>
                        <td class="num">${kp.invoice_count ?? 0}</td>
                    </tr>
                </tfoot>
            </table>
        `;
        printReportDocument({
            title: lang.startsWith('fr') ? 'Déclaration TVA' : 'VAT Report',
            subtitle: period,
            metaHtml,
            contentHtml,
            language: lang,
        });
    };

    const k = data?.kpis;

    return (
        <div data-testid="vat-report-page" className="space-y-6 animate-fadeIn">
            <div className="flex items-center justify-between no-print">
                <div>
                    <Link to="/reports" className="text-xs text-gray-500 hover:text-[#C08478] flex items-center gap-1 mb-2">
                        <ArrowLeft size={12} /> {t('entities:reports.hub.title')}
                    </Link>
                    <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">{t('entities:reports.hub.cards.vat.title')}</h1>
                    <p className="text-gray-500 text-sm mt-1">{t('entities:reports.hub.cards.vat.description')}</p>
                </div>
                <ExportBar onCsv={exportCsv} onPrint={handlePrint} disabled={!data?.by_rate?.length} />
            </div>

            <DateRangeBar
                start={filters.start}
                end={filters.end}
                onChange={({ start, end }) => setFilters((f) => ({ ...f, start, end }))}
                onPreset={setPreset}
            >
                <Select value={filters.rate} onValueChange={(v) => setFilters((f) => ({ ...f, rate: v }))}>
                    <SelectTrigger className="h-9 w-36" data-testid="filter-rate"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('entities:reports.vat.all_rates', { defaultValue: 'All rates' })}</SelectItem>
                        <SelectItem value="20">20%</SelectItem>
                        <SelectItem value="10">10%</SelectItem>
                        <SelectItem value="5.5">5,5%</SelectItem>
                        <SelectItem value="2.1">2,1%</SelectItem>
                        <SelectItem value="0">0% / {t('entities:reports.vat.exempt', { defaultValue: 'Exempt' })}</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={filters.sale_type} onValueChange={(v) => setFilters((f) => ({ ...f, sale_type: v }))}>
                    <SelectTrigger className="h-9 w-36" data-testid="filter-sale-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('entities:reports.sales.filters.all_types', { defaultValue: 'All types' })}</SelectItem>
                        <SelectItem value="invoice">{t('entities:reports.sales.filters.invoice', { defaultValue: 'Invoices' })}</SelectItem>
                        <SelectItem value="sale">{t('entities:reports.sales.filters.sale', { defaultValue: 'Quick sales' })}</SelectItem>
                    </SelectContent>
                </Select>
            </DateRangeBar>

            {loading && <ReportLoader />}
            {error && !loading && <ReportError message={error} onRetry={load} />}

            {!loading && !error && k && (
                <>
                    {/* CA-3 style header */}
                    <Card className="border-0 shadow-sm print-card">
                        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <p className="text-[10px] uppercase tracking-[2px] text-[#C08478] font-semibold">UNIVERS COSMETIC</p>
                                <p className="text-sm text-gray-500 mt-1">{t('entities:reports.vat.declaration_title', { defaultValue: 'TVA — Synthèse périodique' })}</p>
                            </div>
                            <div className="md:text-center">
                                <p className="text-[10px] uppercase tracking-[2px] text-gray-400 font-semibold">{t('entities:reports.statement.period', { defaultValue: 'Period' })}</p>
                                <p className="font-medium">{filters.start && format(filters.start, 'dd MMM yyyy')} — {filters.end && format(filters.end, 'dd MMM yyyy')}</p>
                            </div>
                            <div className="md:text-right">
                                <p className="text-[10px] uppercase tracking-[2px] text-gray-400 font-semibold">{t('entities:reports.statement.generated', { defaultValue: 'Generated' })}</p>
                                <p className="font-medium">{new Date().toLocaleDateString()}</p>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                        <KPICard testId="kpi-ht" icon={Wallet} accent="info" label={t('entities:reports.vat.kpi.grand_ht', { defaultValue: 'Net (HT)' })} value={fmtMoney(k.grand_ht)} />
                        <KPICard testId="kpi-tva" icon={Receipt} accent="warning" label={t('entities:reports.vat.kpi.grand_tva', { defaultValue: 'VAT collected' })} value={fmtMoney(k.grand_tva)} />
                        <KPICard testId="kpi-ttc" icon={TrendingUp} accent="success" label={t('entities:reports.vat.kpi.grand_ttc', { defaultValue: 'Gross (TTC)' })} value={fmtMoney(k.grand_ttc)} />
                        <KPICard testId="kpi-exempt" icon={FileText} accent="default" label={t('entities:reports.vat.kpi.exempt', { defaultValue: 'Exempt sales' })} value={fmtMoney(k.exempt_ttc)} />
                        <KPICard testId="kpi-invoice-count" icon={FileText} accent="accent" label={t('entities:reports.columns.invoice')} value={k.invoice_count} hint={`${k.rate_count} ${t('entities:reports.vat.rates_active', { defaultValue: 'rates' })}`} />
                    </div>

                    {/* By rate table */}
                    <Card className="border-0 shadow-sm">
                        <CardHeader><CardTitle className="text-sm">{t('entities:reports.vat.by_rate_title', { defaultValue: 'Breakdown by VAT rate' })}</CardTitle></CardHeader>
                        <CardContent>
                            {data.by_rate.length === 0 ? <ReportEmpty message={t('entities:reports.empty')} /> : (
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-[#F5F5F5]">
                                            <TableHead>{t('entities:reports.vat.rate', { defaultValue: 'Rate' })}</TableHead>
                                            <TableHead className="text-right">{t('entities:reports.vat.ht', { defaultValue: 'Net HT' })}</TableHead>
                                            <TableHead className="text-right">{t('entities:reports.vat.tva', { defaultValue: 'VAT' })}</TableHead>
                                            <TableHead className="text-right">{t('entities:reports.vat.ttc', { defaultValue: 'Gross TTC' })}</TableHead>
                                            <TableHead className="text-right">{t('entities:reports.columns.invoice')}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {data.by_rate.map((b) => (
                                            <TableRow key={b.rate} data-testid={`vat-rate-${b.rate}`}>
                                                <TableCell className="font-semibold">{b.rate}%</TableCell>
                                                <TableCell className="text-right">{fmtMoney(b.ht)}</TableCell>
                                                <TableCell className="text-right text-[#E9C46A] font-semibold">{fmtMoney(b.tva)}</TableCell>
                                                <TableCell className="text-right font-semibold">{fmtMoney(b.ttc)}</TableCell>
                                                <TableCell className="text-right text-gray-500">{b.invoice_count}</TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow className="bg-[#1A1A1A] text-white">
                                            <TableCell className="font-bold text-white">Total</TableCell>
                                            <TableCell className="text-right font-bold text-white">{fmtMoney(k.grand_ht)}</TableCell>
                                            <TableCell className="text-right font-bold text-white">{fmtMoney(k.grand_tva)}</TableCell>
                                            <TableCell className="text-right font-bold text-white">{fmtMoney(k.grand_ttc)}</TableCell>
                                            <TableCell className="text-right text-white">{k.invoice_count}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>

                    {/* Monthly chart */}
                    <Card className="border-0 shadow-sm">
                        <CardHeader><CardTitle className="text-sm">{t('entities:reports.vat.monthly_title', { defaultValue: 'Monthly breakdown' })}</CardTitle></CardHeader>
                        <CardContent>
                            {data.monthly.length === 0 ? <ReportEmpty message={t('entities:reports.empty')} /> : (
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={data.monthly}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                                        <YAxis tick={{ fontSize: 11 }} />
                                        <Tooltip formatter={(v) => fmtMoney(v)} />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        <Bar dataKey="ht" name={t('entities:reports.vat.ht', { defaultValue: 'Net HT' })} fill="#264653" radius={[4, 4, 0, 0]} />
                                        <Bar dataKey="tva" name={t('entities:reports.vat.tva', { defaultValue: 'VAT' })} fill="#E9C46A" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
};

export default VatReport;
